import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Modal, Spinner } from 'react-bootstrap';
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs/components/prism-core';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-javascript';

import { getCode, publishCode } from '../../services/update-site';
import { ErrorBox } from '../ErrorBox/ErrorBox';
import CustomTooltip from '../CustomTooltip';
import { showUpgradeModal } from '../../Manage/actions';

import './prism.css';
import './CodeEditor.css';



const CodeEditor = ({ show, onHide, file = '', domain, trialMode, dispatch }) => {
  const [code, setCode] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [errorPublishing, setErrorPublishing] = useState(false);
  const [hasErrored, setHasErrored] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fetchCode = () => {
    getCode({ file, domain: domain?.subdomain })
      .then(({ data: { code } }) => {
        setCode(code);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
        setHasErrored(true);
      });
  };

  const onPublish = () => {
    setErrorPublishing(false);
    setIsPublishing(true);
    publishCode({ code, file, domain: domain?.subdomain })
      .then(() => {
        setIsDirty(false);
        setIsPublishing(false);
      })
      .catch(() => {
        setIsPublishing(false);
        setErrorPublishing(true);
      });
  };

  const getDomainUrl = (domain) => {
    if (domain && domain.subdomain) {
      return `https://${domain.subdomain}`;
    }
    return '';
  };

  useEffect(() => {
    if (show) {
      fetchCode();
      setIsLoading(true);
    }
  }, [show]);

  const onDiscard = () => {
    setCode('');
    setIsDirty(false);
    setIsPublishing(false);
    setErrorPublishing(false);
    setHasErrored(false);
    setIsLoading(true);
    onHide();
  };

  const onCodeChange = (code) => {
    if (!isDirty) {
      setIsDirty(true);
    }
    setCode(code);
  };

  const onUpgrade = () => {
    dispatch(showUpgradeModal(true));
    onHide();
  };

  return (
    <Modal
      className="p-5"
      backdrop="static"
      size="lg"
      show={show}
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <div className="p-3">
        {isLoading && <Spinner className="ms-2" animation="border" variant="primary" />}

        <>
          <div style={{ display: 'flex' }}>
            <h5 className="mb-3">{file.split('/').pop()}</h5>
            <a className="ms-3" href={getDomainUrl(domain)} target="_blank" rel="noopener noreferrer">
              View site
            </a>
            {!isDirty && (
              <CustomTooltip label="Latest">
                <img className="puff-in-center ms-auto" src="assets/icons/check.svg" height="25px" alt="success" />
              </CustomTooltip>
            )}
          </div>
          <div className="editor-container">
            <Editor
              placeholder="Type some code…"
              value={code}
              onValueChange={onCodeChange}
              highlight={(code) => highlight(code, languages.js)}
              padding={10}
              style={{
                fontFamily: '"Fira code", "Fira Mono", monospace',
                fontSize: 12,
                background: '#f3f3f3',
                borderRadius: '4px',
                overflow: 'scroll',
              }}
              disabled={trialMode}
            />
             {trialMode && <div className="promo-cover text-center">
            <h4 className="promo-text">Quickly edit & publish your code with Pro</h4>
            <Button className="mt-3" onClick={onUpgrade}>Upgrade to Pro</Button>
          </div>
          } 
          </div>
          <div className="mt-3">
            {isDirty ? (
              <>
                {errorPublishing && <div style={{ color: 'red' }}>Unable to publish, please try again.</div>}
                <div>
                  <Button className="ms-2 float-right" onClick={onPublish} disabled={isPublishing}>
                    Publish {isPublishing && <Spinner animation="border" variant="light" size="sm" />}
                  </Button>
                  <Button className="float-right" variant="outline-danger" onClick={onDiscard} disabled={isPublishing}>
                    Discard
                  </Button>
                </div>
              </>
            ) : (
              <Button className="float-right" variant="outline-primary" onClick={onDiscard}>
                Close
              </Button>
            )}
          </div>
        </>

        {hasErrored && <ErrorBox onClick={onHide} />}
      </div>
    </Modal>
  );
};

export default connect(null, (dispatch) => ({ dispatch }))(CodeEditor);
